import React, { useEffect, useState } from 'react'
import { Button, Card, DatePicker, Empty, Form, Modal, Popconfirm, Select, Spin, Table, Tag, message } from 'antd'
import moment from 'moment'
import AssetConditionForm from './AssetConditionForm'
import { createVoucher, errorMessage, fetchReservedConditions, fetchSchema } from './api'
import PhotoThumbs from './PhotoThumbs'
import { BASE_PATH, assetLabel, idOf } from './helpers'

const range = (start, end) => Array.from({ length: Math.max(end - start, 0) }, (v, i) => start + i)

const disableFutureDate = (current) => !!current && current.isAfter(moment(), 'day')

// For today, also block hours / minutes that are still ahead of now
const disableFutureTime = (current) => {
  const now = moment()
  if (!current || !current.isSame(now, 'day')) return {}
  return {
    disabledHours: () => range(now.hour() + 1, 24),
    disabledMinutes: (hour) => hour === now.hour() ? range(now.minute() + 1, 60) : []
  }
}

const validateNotFuture = (rule, value, callback) => {
  if (value && value.isAfter(moment(), 'minute')) callback('Date cannot be in the future')
  else callback()
}

const refOptions = (schema, key) => (schema && schema[key] && schema[key].data && schema[key].data.data) || []

const AcvCreate = ({ form, history }) => {
  const { getFieldDecorator, getFieldValue } = form
  const [schema, setSchema] = useState(null)
  const [conditions, setConditions] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [records, setRecords] = useState([])

  useEffect(() => {
    Promise.all([fetchSchema(), fetchReservedConditions()])
      .then(([schemaData, conditionData]) => {
        setSchema(schemaData)
        setConditions(conditionData || [])
        if (!conditionData || !conditionData.length) {
          message.error('No reserved conditions found for this organisation. Please contact your administrator.')
        }
      })
      .catch((e) => message.error(errorMessage(e, 'Failed to load form data')))
      .finally(() => setLoading(false))
  }, [])

  const location = getFieldValue('location')
  const client = getFieldValue('client')
  const locations = refOptions(schema, 'location')
  // Sub locations tied to another location are hidden; unlinked ones are always shown
  const clients = refOptions(schema, 'client').filter((c) => !location || !idOf(c.location) || String(idOf(c.location)) === String(location))

  // Records are scoped to the location / sub location, so lock them once a record is added
  const locked = records.length > 0

  const handleLocationChange = () => form.setFieldsValue({ client: undefined })

  const removeRecord = (key) => setRecords((prev) => prev.filter((r) => r.key !== key))

  const goBack = () => history.push(BASE_PATH)

  const handleCancel = () => {
    if (!records.length) return goBack()
    Modal.confirm({
      title: 'Discard this voucher?',
      content: `${records.length} added record(s) will be lost.`,
      okText: 'Discard',
      okType: 'danger',
      onOk: goBack
    })
  }

  const handleSave = () => {
    form.validateFields((err, values) => {
      if (err) return
      if (!records.length) {
        message.error('Add at least one asset condition record')
        return
      }
      const payload = {
        date: values.date.toISOString(),
        location: values.location,
        client: values.client || undefined,
        assetConditions: records.map((r) => ({
          asset: r.asset._id,
          condition: r.condition._id,
          entryMode: r.entryMode,
          remarks: r.remarks,
          latitude: r.latitude,
          longitude: r.longitude,
          geoSource: r.geoSource,
          photos: r.photos,
          createdAt: r.createdAt
        }))
      }
      setSaving(true)
      createVoucher(payload)
        .then((saved) => {
          message.success(`${saved.acvNo} saved`)
          history.push(`${BASE_PATH}/view/${saved._id}`)
        })
        .catch((e) => {
          message.error(errorMessage(e, 'Failed to save the voucher'))
          setSaving(false)
        })
    })
  }

  const columns = [
    { title: '#', key: 'index', width: 50, render: (text, record, index) => index + 1 },
    { title: 'Asset', key: 'asset', render: (r) => <span>{assetLabel(r.asset)}{r.entryMode === 'QR' && <Tag className='acv-qr-tag'>QR</Tag>}</span> },
    {
      title: 'Condition',
      key: 'condition',
      render: (r) => <Tag color={r.condition.color || undefined}>{r.condition.name}</Tag>
    },
    { title: 'Remark', dataIndex: 'remarks', key: 'remarks' },
    {
      title: 'Geo Location',
      key: 'geo',
      render: (r) => r.latitude !== undefined
        ? `${r.latitude.toFixed(6)}, ${r.longitude.toFixed(6)}${r.geoSource === 'MAP' ? ' (map)' : ''}`
        : '-'
    },
    {
      title: 'Photos',
      key: 'photos',
      render: (r) => <PhotoThumbs photos={r.photos || []} />
    },
    { title: 'Added At', key: 'createdAt', render: (r) => moment(r.createdAt).format('DD-MM-YYYY HH:mm:ss') },
    {
      title: 'Action',
      key: 'action',
      render: (r) => (
        <Popconfirm title='Remove this record?' onConfirm={() => removeRecord(r.key)} okText='Yes' cancelText='No'>
          <Button type='danger' size='small'>Remove</Button>
        </Popconfirm>
      )
    }
  ]

  return (
    <Spin spinning={loading || saving}>
      <Card className='gx-card-metrics' title='CREATE ASSET CONDITION VOUCHER'>
        <Form layout='vertical'>
          <div className='acv-header-grid'>
              <Form.Item label='Date'>
                {getFieldDecorator('date', {
                  rules: [
                    { required: true, message: 'Date is required!' },
                    { validator: validateNotFuture }
                  ],
                  initialValue: moment()
                })(
                  <DatePicker
                    showTime
                    format='DD-MM-YYYY HH:mm'
                    style={{ width: '100%' }}
                    disabledDate={disableFutureDate}
                    disabledTime={disableFutureTime}
                  />
                )}
              </Form.Item>
              <Form.Item label='Location'>
                {getFieldDecorator('location', {
                  rules: [{ required: true, message: 'Location is required!' }]
                })(
                  <Select showSearch optionFilterProp='children' placeholder='Select location' onChange={handleLocationChange} disabled={locked}>
                    {locations.map((l) => <Select.Option key={l._id} value={l._id}>{l.name}</Select.Option>)}
                  </Select>
                )}
              </Form.Item>
              <Form.Item label='Sub Location'>
                {getFieldDecorator('client')(
                  <Select showSearch allowClear optionFilterProp='children' placeholder='Select sub location' disabled={locked}>
                    {clients.map((c) => <Select.Option key={c._id} value={c._id}>{c.name}</Select.Option>)}
                  </Select>
                )}
              </Form.Item>
          </div>
        </Form>
        {locked && <div className='acv-help'>Location and sub location are locked while records are added. Remove all records to change them.</div>}

        <h4 className='acv-section-title'>Asset Conditions ({records.length})</h4>
        {records.length
          ? <Table size='small' rowKey='key' columns={columns} dataSource={records} pagination={false} scroll={{ x: true }} />
          : <Empty description='No records added yet' />}

        <Card size='small' className='acv-record-card' title='Add Asset Condition'>
          <AssetConditionForm
            conditions={conditions}
            location={location}
            client={client}
            addedAssetIds={records.map((r) => String(r.asset._id))}
            onAdd={(record) => setRecords((prev) => [...prev, record])}
          />
        </Card>

        <div className='acv-footer'>
          <Button type='primary' icon='save' onClick={handleSave} loading={saving} disabled={!records.length}>
            Save ACV
          </Button>
          <Button onClick={handleCancel} disabled={saving}>Cancel</Button>
        </div>
      </Card>
    </Spin>
  )
}

export default Form.create({ name: 'acv_header' })(AcvCreate)
